import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { Box, Button, EmptyState, Flex, MenuItem, Sparkline, Spinner, Suspense, Text } from 'core/components';
import { Field, FormComponent, Select } from 'core/form';
import Model from 'core/model/Model';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';
import { getAggregateUnitLabel } from 'app/components/metric/metricRenderers';
import $clouds from 'app/stores/clouds/$clouds';
import $devices from 'app/stores/device/$devices';
import { CLOUD_PROVIDERS } from 'app/util/constants';
import { adjustByGreekPrefix, getToFixed, zeroToText } from 'app/util/utils';
import { withRouter } from 'react-router-dom';
import { addFilters } from 'app/stores/query/FilterUtils';
import { isEqual } from 'lodash';
import WidgetFrame from '../WidgetFrame';

const FORM_OPTIONS = {
  name: 'Cloud InboundOutbound Widget Config'
};

const ALL_CLOUD_FORM_OPTIONS = [
  {
    value: 'gcp',
    label: 'GCP',
    regionFilter: 'ktsubtype__gcp_subnet__STR04src|dstktsubtype__gcp_subnet__STR05'
  },
  { value: 'azure', label: 'Azure', regionFilter: 'kt_az_src|dst_region' },
  { value: 'aws', label: 'AWS', regionFilter: 'kt_aws_src|dst_region' },
  {
    value: 'oci',
    label: 'OCI',
    regionFilter: 'ktsubtype__oci_subnet__STR10src|dstktsubtype__oci_subnet__STR11'
  }
];

// RBAC-scoped: clouds with a visible active `${provider}_subnet` device. A saved selection that is no longer
// available stays listed so the form can show it, flagged "(removed)" once statistics have loaded.
function getCloudTypeOptions(selectedCloudType) {
  const options = ALL_CLOUD_FORM_OPTIONS.filter((cloud) => $devices.hasUserSubtype(`${cloud.value}_subnet`));

  if (selectedCloudType && !options.some((cloud) => cloud.value === selectedCloudType)) {
    const known = ALL_CLOUD_FORM_OPTIONS.find((cloud) => cloud.value === selectedCloudType);
    const label = known?.label ?? selectedCloudType;
    options.push({
      value: selectedCloudType,
      label: $devices.hasLoadedDeviceStatistics ? `${label} (removed)` : label
    });
  }

  return options;
}

function getValue(seriesOrValue, results, queryModel) {
  const [aggregateType] = queryModel.get('aggregateTypes');
  return zeroToText(
    adjustByGreekPrefix(
      seriesOrValue.get ? seriesOrValue.get(aggregateType) : seriesOrValue,
      results.prefix[queryModel.outsortUnit]
    ),
    {
      fix: getToFixed(queryModel.outsortUnit)
    }
  );
}

function DetailMenuItem({ type, region, history }) {
  return (
    <MenuItem
      icon="series-search"
      text="View Cloud Details"
      onClick={() =>
        history.push(`/v4/core/quick-views/cloud/${type}${region !== 'All Regions' ? `/region/${region}` : ''}`)
      }
    />
  );
}

function Result({ value, row }) {
  const name = row.get('key');
  return (
    <Flex alignItems="center" justifyContent="space-between" color="muted" mb="4px">
      <Text>{name.charAt(0).toUpperCase() + name.slice(1)}</Text>
      <Text alignItems="center" textAlign="right">
        <Text>{value}</Text>
      </Text>
    </Flex>
  );
}

@inject('$query')
@withRouter
@observer
class CloudTrafficProfileWidget extends Component {
  static fields = {
    cloud_type: () => {
      const options = getCloudTypeOptions();
      const defaultValue = options.length > 0 ? options[0].value : '';
      return {
        label: 'Cloud',
        rules: 'required',
        options,
        defaultValue,
        getFieldProps(config, { prefix = '' } = {}) {
          const onChange = (field, value, prev, form) => {
            form.setValue(`${prefix && `${prefix}.`}cloud_region`, 'All Regions');
          };
          // computed on every call so options follow statistics changes while the form is open
          return { onChange, options: getCloudTypeOptions(config?.cloud_type) };
        }
      };
    },
    cloud_region: {
      label: 'Region',
      options: [{ value: 'All Regions', label: 'All Regions' }],
      defaultValue: 'All Regions',
      getFieldProps(config) {
        const { cloud_type } = config;
        const options = [{ value: 'All Regions', label: 'All Regions' }];
        if (cloud_type) {
          const cloudRegions = $clouds.getCloudRegions([cloud_type])[cloud_type];
          if (cloudRegions) {
            options.push(...cloudRegions.map((region) => ({ value: region, label: region })));
          }
        }
        return { options };
      }
    }
  };

  static getDefaultTitle(config) {
    return config.cloud_type
      ? `${CLOUD_PROVIDERS.byId(config.cloud_type).code}
        ${config.cloud_region ? ` - ${config.cloud_region}` : ''}`
      : 'Select Cloud Source';
  }

  state = {
    loading: false,
    queryOverrides: {
      lookback_seconds: 86400,
      aggregateType: 'max_bits_per_sec'
    }
  };

  static getDerivedStateFromProps(props, state) {
    const { config } = props;
    // If there's no config in state, let's get setup
    if (!state.config) {
      // If there's no config, we show the config panel
      if (!config) {
        return { config: {}, isConfigurePanelOpen: true, model: new Model() };
      }
      return { config, model: new Model(config) };
    }
    if (config && !isEqual(config, state.config)) {
      return { config, model: new Model(config) };
    }

    return null;
  }

  handleSave = (form) => {
    const { onConfigChange } = this.props;
    const { model } = this.state;

    const config = form.getValues();
    model.set(config);

    this.setState({ isConfigurePanelOpen: false, config });

    if (onConfigChange) {
      onConfigChange(config);
    }
  };

  handleCancel = (form) => {
    form.reset();
    this.setState({ isConfigurePanelOpen: false });
  };

  handleShowConfigurePanel = () => {
    this.setState({ isConfigurePanelOpen: true });
  };

  renderConfigurePanel() {
    const { model, config } = this.state;
    const { fields } = CloudTrafficProfileWidget;

    Object.keys(fields).forEach((fieldKey) => {
      const field = fields[fieldKey];
      if (typeof field === 'function') {
        fields[fieldKey] = field();
      }
    });

    return (
      <FormComponent fields={fields} options={FORM_OPTIONS} model={model}>
        {({ form }) => (
          <Flex flexDirection="column" flex={1}>
            <Box flex={1}>
              <Field name="cloud_type" large {...fields.cloud_type.getFormProps(config)}>
                <Select />
              </Field>
              <Field name="cloud_region" large placeholder="All Regions" {...fields.cloud_region.getFormProps(config)}>
                <Select />
              </Field>
            </Box>
            <Flex pb={2}>
              {config.cloud_type && (
                <Button text="Cancel" onClick={() => this.handleCancel(form)} mr={1} minWidth={100} />
              )}
              <Button
                text="Save"
                intent="primary"
                disabled={!form.valid}
                onClick={() => this.handleSave(form)}
                minWidth={100}
              />
            </Flex>
          </Flex>
        )}
      </FormComponent>
    );
  }

  renderSparkline(results, queryModel) {
    const { config } = this.state;
    const { prefix } = results;
    const [aggregateType] = queryModel.get('aggregateTypes');
    const [aggregate] = queryModel.aggregates;
    const [totalOverlay] = results.overlayRows;
    const total = (totalOverlay && totalOverlay.get(aggregateType)) || 0;

    const [, ...aggTypeParts] = aggregateType.split('_');
    const rawDataAttr = `rawData.both_${aggTypeParts.join('_')}.flow`;

    const sparklineData =
      totalOverlay && totalOverlay.get(rawDataAttr) ? totalOverlay.get(rawDataAttr).map((col) => col[1]) : [];

    const cloudProvider = CLOUD_PROVIDERS.byId(config.cloud_type);

    return (
      <>
        <cloudProvider.logoCmp
          style={{
            width: 120,
            height: 120,
            position: 'absolute',
            left: 'calc(50% - 60px)',
            top: 'calc(50% - 50px)',
            opacity: 0.1
          }}
        />

        <Flex
          position="absolute"
          left={0}
          bottom={0}
          flexDirection="column"
          justifyContent="flex-end"
          width="100%"
          overflow="hidden"
        >
          <Sparkline
            data={sparklineData}
            strokeWidth={1.5}
            height={25}
            margin={4}
            color={cloudProvider.color || 'primary'}
          />
        </Flex>

        <Flex justifyContent="flex-end" color="muted" fontSize="small" mb="4px">
          {getAggregateUnitLabel({ aggregate, prefix })}
        </Flex>
        <Flex alignItems="center" justifyContent="space-between" mb="4px">
          <Text fontWeight="bold">Total</Text>
          <Text fontWeight="bold" alignItems="center" textAlign="right">
            <Text>{getValue(total, results, queryModel)}</Text>
          </Text>
        </Flex>

        {results.nonOverlayRows.map((row) => {
          const value = getValue(row, results, queryModel);
          return <Result key={row.id} row={row} value={value} />;
        })}
      </>
    );
  }

  renderData() {
    const { $query } = this.props;
    const { queryOverrides, isConfigurePanelOpen, config } = this.state;
    if (!config.cloud_type || isConfigurePanelOpen) {
      return <EmptyState description="Please select a cloud source to view traffic profiles." />;
    }

    const updatedQueryOverrides = Object.assign({}, queryOverrides, {
      filterDimensionsEnabled: false,
      metric: ['simple_trf_prof'],
      all_devices: false,
      device_types: [`${config.cloud_type}_subnet`]
    });

    const query = $query.get('inboundOutboundInternalQuery', updatedQueryOverrides);
    if (config.cloud_region !== 'All Regions') {
      addFilters(query, [
        {
          filterField: ALL_CLOUD_FORM_OPTIONS.find((cloud) => cloud.value === config.cloud_type)?.regionFilter,
          operator: '=',
          filterValue: `${config.cloud_region}`
        }
      ]);
    }

    return (
      <LightweightDataViewWrapper query={query}>
        {({ loading, results, queryModel }) => (
          <Suspense loading={loading} fallback={<Spinner size={24} />}>
            {this.renderSparkline(results, queryModel)}
          </Suspense>
        )}
      </LightweightDataViewWrapper>
    );
  }

  render() {
    const { canCustomize, onRemove, history, title, ...props } = this.props;
    const { isConfigurePanelOpen, loading, config } = this.state;
    const tooltip =
      'Provides an at-a-glance view of the total traffic of your [AWS, Azure, GCP, etc] environment, broken out by various traffic profiles.';

    return (
      <WidgetFrame
        canCustomize={canCustomize}
        menuOptions={<DetailMenuItem type={config.cloud_type} region={config.cloud_region} history={history} />}
        configAction={this.handleShowConfigurePanel}
        onRemove={onRemove}
        title={title || CloudTrafficProfileWidget.getDefaultTitle(config)}
        tooltip={tooltip}
        display="flex"
        flexDirection="column"
        position="relative"
        pt={1}
        px={2}
        pb={0}
        {...props}
      >
        <Suspense
          loading={loading}
          fallback={
            <Box pt={2}>
              <Spinner size={24} />
            </Box>
          }
        >
          {isConfigurePanelOpen && this.renderConfigurePanel()}
          {!isConfigurePanelOpen && this.renderData()}
        </Suspense>
      </WidgetFrame>
    );
  }
}

export default CloudTrafficProfileWidget;
